import { supertypeClass, property, Remoteable, Supertype } from '@haventech/amorphic';
import { PolicyAdminPolicy, PolicyInfo } from './PolicyAdminClient';
import { TPolicyAdminPartyInfo } from '../interfaces/CommonTypes';
import { PolicyAdminRiders } from './PolicyAdminRiders';
import { TrackerStatus } from '../../../coverpath-agent/public/js/types/CaseDetailsType';

const BILLING_STAGES: { [key: string]: string } = {
  'TLIC Billing Setup (BS1 TLIC Billing Setup)': null,
  'Policy Billing Setup (BS2 Policy Billing Setup)': null,
  'Billing Setup(TLIC) (BS3 Awaiting Recurring Setup)': null,
  'Billing Setup(TLIC) (BS4 Awaiting Recurring Setup with Failed Payment)': null,
  'Billing Recurring (BR1 Billing Recurring)': null,
  'Billing Recurring (BR2 Billing Recurring with Failed Payment)': null,
  'Awaiting Termination (BC1 Awaiting Termination)': null,
  'Billing Grace (BG2 Billing Grace)': null,
  'Billing Grace (BG1 Awaiting Billing Grace)': null,
  'Billing Terminated (BT1 Billing Terminated)': null,
};

const POLICY_STAGES: { [key: string]: string } = {
  'Coverage Issuance (CI1 TLIC)': null,
  'Coverage Issuance (CI2 Policy)': null,
  'Coverage Issuance (CI3 Awaiting TLIC Cancellation)': null,
  'In Force (IF1 Free Look)': 'freeLook',
  'In Force (IF2 Active)': 'policyInForce',
  'In Force (IF3 Awaiting Free Look Cancellation)': 'policyInForce',
  'In Force (IF4 Awaiting TLIC Declination)': 'policyInForce',
  'In Force (IF5 Awaiting Policy Cancellation)': 'policyInForce',
  'Reinstatable (RE1 Lapsed)': 'gracePeriod',
  'Terminated (TM1 Terminated)': 'rejected',
  'Terminated (TM2 Cancelled Free Look)': 'userPolicyCancel',
  'Terminated (TM3 Cancelled TLIC)': 'userPolicyCancel',
  'Terminated (TM4 Declined TLIC)': 'declined',
  'Terminated (TM5 Cancelled Policy)': 'userPolicyCancel',
  'Terminated (TM6 Not taken no full first payment)': 'declined',
};

const STATUS_DELIMITER: string = ':';

const STATE_MAPPER = {
  CI1: {
    BR1: 'pendingPolicyDocsPA',
  },
  CI2: 'pendingPolicyDocsPA',
  IF1: {
    BR1: 'freeLook',
    BR2: 'notCovered',
    BG2: 'gracePeriod',
  },
  IF2: {
    BR1: 'policyInForce',
    BR2: 'notCovered',
    BG1: 'policyInForce',
    BG2: 'gracePeriod',
    BT1: 'catchallPA',
  },
  IF5: 'policyInForce',
  RE1: 'autoPolicyCancel',
  TM2: 'userPolicyCancel',
  TM5: 'userPolicyCancel',
  TM6: 'userPolicyCancel',
  TM8: 'userAppCancel',
};

const WORKFLOW_STATE_MAPPER = {
  'IF1:BR1': 'In Force:Free Look Period',
  'IF2:BR1': 'In Force:Issued',
  'CI1:BR1': 'Application:Issuing Policy',
  'CI2:ANY': 'In Force:Pending Policy Docs',
  'IF5:BC1': 'In Force:Issued',
  'IF1:BG2': 'In Force:Grace Period',
  'IF2:BG2': 'In Force:Grace Period',
  'IF2:BG1': 'In Force:Issued',
  'RE1:ANY': 'Terminated:Lapsed',
  'TM2:ANY': 'Terminated:Canceled - Free Look',
  'TM6:ANY': 'Terminated:Not Taken',
  'TM5:ANY': 'Terminated:Canceled',
  'TM4:ANY': 'Terminated:Rescinded',
  'IF3:ANY': 'Terminated:Canceled',
  'TM12:ANY': 'Terminated:Conversion_completed',
};

const WORKFLOW_STATE_MAPPER_CP_AGENT = {
  'IF1:BR1': 'Free_Look', //free look
  'IF2:BR1': 'In_Force', //in force
  'CI1:BR1': 'In_Force', //in force
  'CI2:ANY': 'In_Force:Pending_Doc_Ready', //in force: doc is not ready yet.
  'IF5:BC1': 'In_Force', //in force
  'IF1:BG2': 'Grace Period', //grace period
  'IF2:BG2': 'Grace Period', //grace period
  'IF2:BG1': 'In_Force', //in force
  'IF1:BR2': 'In_Force:Recurring_Payment_Failed', //free look
  'IF2:BR2': 'In_Force:Recurring_Payment_Failed', //in force
  'RE1:ANY': 'Terminated:Lapsed', //Terminated:Lapsed
  'TM2:ANY': 'Terminated:Canceled_By_User', //canceled by user
  'TM6:ANY': 'Terminated:Not_Taken', //never payed
  'TM5:ANY': 'Terminated:Canceled_By_User', //canceled by user
  'TM4:ANY': 'Terminated:Rescinded', //cancelled by system
  'IF3:ANY': 'Terminated:Canceled_By_User', //canceled by user
  'IF3:BL1': 'In_Force:Billing_Lapsed',
  'IF3:BR1': 'In_Force:Billing_Lapsed',
  'TM10:ANY': 'Terminated:Lapsed_No_Payment_3Years', // Terminated: Lapsed after no client payment for 3 years
  'TM12:ANY': 'Terminated:Conversion_completed', // Terminated: Conversion_completed; show as canceled by system
};

const WORKFLOW_STATE_MAPPER_CASE_DETAILS = {
  'IF1:BR1': TrackerStatus.InForceFreeLook, //free look
  'IF2:BR1': TrackerStatus.InForce, //in force
  'CI1:BR1': TrackerStatus.InForce, //in force
  'CI2:ANY': TrackerStatus.SettingUpPolicyDocuments, //in force: doc is not ready yet.
  'IF5:BC1': TrackerStatus.InForce, //in force
  'IF1:BG2': TrackerStatus.Grace, //grace period
  'IF2:BG2': TrackerStatus.Grace, //grace period
  'IF2:BG1': TrackerStatus.InForce, //in force
  'IF1:BR2': TrackerStatus.CoveragePending, //free look
  'IF2:BR2': TrackerStatus.CoveragePending, //in force
  'RE1:ANY': TrackerStatus.PolicyLapsed, //Terminated:Lapsed
  'TM2:ANY': TrackerStatus.PolicyCanceledByClient, //canceled by user
  'TM6:ANY': TrackerStatus.OfferNotTaken, //never payed
  'TM5:ANY': TrackerStatus.PolicyCanceledByClient, //canceled by user
  'TM4:ANY': TrackerStatus.PolicyRescinded, //cancelled by system
  'IF3:ANY': TrackerStatus.PolicyCanceledByClient, //canceled by user
  'TM10:ANY': TrackerStatus.PolicyLapsed, // Terminated: Lapsed after no client payment for 3 years
  'TM12:ANY': TrackerStatus.PolicyCanceledAfterConversion, // Terminated: Conversion_completed; show as canceled by system
  'CL2:ANY': TrackerStatus.DeathClaimPaid,
};
const POLICY_GENERAL_STATUS_MAPPER_CP_AGENT = {
  GRACEPENDING: 'In_Force',
  DeathClaim: 'DeathClaim',
  Rescission: 'Rescission',
  WaiverOfPremium: 'WaiverOfPremium',
  NotFound: 'Not Found',
};

export const POLICY_DOC_READY_LIST: Array<string> = [
  'In Force:Grace Period', // 'IF1:BG2' & 'IF2:BG2'
  'In Force:Issued', // 'IF2:BG1' & 'IF2:BR1' & 'IF5:BC1'
  'In Force:Free Look Period', // 'IF1:BR1'
  'In_Force:Recurring_Payment_Failed', // 'IF1:BR2', 'IF2:BR2'
  'Terminated:Lapsed', //RE1:ANY
];

@supertypeClass({ toClient: true, toServer: false })
export class PolicyAdminStatus extends Remoteable(Supertype) {
  public static readonly POLICY_ADMIN_RESPONSE_STATUSES: { [key: string]: string } = {
    OK: 'OK',
    ERROR: 'ERROR',
    QUEUED: 'QUEUED',
  };

  @property()
  private policyStage: string;

  @property()
  private billingStage: string;

  @property()
  private nextPaymentDate: Date;

  @property()
  private issuedDate: Date;

  @property()
  private tlicEffectiveDate: Date;

  @property()
  private effectiveDate: Date;

  @property()
  private terminationDate: Date;

  @property()
  private freeLookEndDate: Date;

  @property()
  private graceStartDate: Date;

  @property()
  private graceEndDate: Date;

  @property()
  private lapsedDate: Date;

  @property()
  private conversionEndDate: Date;

  @property()
  private conversionCreditPercent: number;

  @property()
  private conversionCreditExpirationDate: Date;

  @property()
  private acceleratedBenefitRider: boolean;

  @property()
  private policyStageCode: string;

  @property()
  private billingStageCode: string;

  @property()
  private responseStatus: string;

  @property()
  private isPolicyAdminPolicy: boolean;

  @property()
  private policyHoldStatus: string;

  @property()
  private hasThirdPartyPayer: boolean;

  @property()
  private hasNoPayer: boolean;

  @property()
  private ownerIsEntity: boolean;

  @property()
  private policyAdminPolicyId: string;

  @property()
  private policyNumber: string;

  @property()
  private faceAmount: number;

  @property()
  private rateClass: string;

  @property()
  private waiverOfPremium: boolean;

  @property()
  private ownerFirstName: string;

  @property()
  private ownerLastName: string;

  @property()
  private duration: number;

  @property()
  private basePremiumAmount: number;

  @property()
  private waiverPremiumAmount: number;

  @property()
  private substandardPremiumAmount: number;

  @property()
  private feeAmount: number;

  @property()
  private flatExtraAmount: number;

  @property()
  private paidToDate: Date;

  @property()
  private lastUpdatedTimeOnParty: Date;

  @property()
  private policyGeneralStatus: string;

  @property()
  private paymentType: string;

  @property()
  private paymentFrequency: string;

  // @TODO remove this once cp starts using party data from eventor
  // NOTE that we only plan to use owners and insured properties on the backend
  //      that's why we can use a type (TPolicyAdminPartyInfo) instead of supertype class
  @property({ getType: () => Object })
  owners: Array<TPolicyAdminPartyInfo>;

  @property({ getType: () => PolicyAdminRiders })
  policyAdminRiders: PolicyAdminRiders;

  @property()
  insured: TPolicyAdminPartyInfo;

  constructor() {
    super();
  }

  static new(policyAdminPolicy: PolicyAdminPolicy) {
    return new PolicyAdminStatus().init(policyAdminPolicy);
  }

  init(policyAdminPolicy: PolicyAdminPolicy) {
    this.responseStatus = policyAdminPolicy.responseStatus;
    this.isPolicyAdminPolicy = policyAdminPolicy.isPolicyAdminPolicy;
    this.policyHoldStatus = policyAdminPolicy.policyHoldStatus;

    this.hasThirdPartyPayer = policyAdminPolicy.hasThirdPartyPayer;
    this.hasNoPayer = policyAdminPolicy.hasNoPayer;
    this.ownerIsEntity = policyAdminPolicy.ownerIsEntity;

    this.rateClass = policyAdminPolicy.rateClass;
    this.waiverOfPremium = policyAdminPolicy.waiverOfPremium;
    this.acceleratedBenefitRider = policyAdminPolicy.acceleratedBenefitRider;

    if (policyAdminPolicy.policyInfo) {
      this.policyAdminPolicyId = policyAdminPolicy.policyInfo.policyAdminPolicyId;
      this.policyNumber = policyAdminPolicy.policyInfo.policyNumber;

      this.derivePolicyStatus(policyAdminPolicy);

      this.issuedDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.issuedDate);
      this.tlicEffectiveDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.tlicEffectiveDate);
      this.effectiveDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.effectiveDate);
      this.terminationDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.terminationDate);
      this.freeLookEndDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.freeLookEndDate);
      this.graceStartDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.graceStartDate);
      this.graceEndDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.graceEndDate);
      this.lapsedDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.lapsedDate);
      this.conversionEndDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.conversionEndDate);
      this.conversionCreditExpirationDate = this.derivePolicyDate(
        policyAdminPolicy.policyInfo.conversionCreditExpirationDate
      );
      this.conversionCreditPercent = policyAdminPolicy.policyInfo.conversionCreditPercent;
      this.nextPaymentDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.nextPaymentDate);
      this.faceAmount = policyAdminPolicy.policyInfo.faceAmount;
      this.duration = policyAdminPolicy.policyInfo.duration;
      this.basePremiumAmount = policyAdminPolicy.policyInfo.basePremiumAmount;
      this.waiverPremiumAmount = policyAdminPolicy.policyInfo.waiverPremiumAmount;
      this.substandardPremiumAmount = policyAdminPolicy.policyInfo.substandardPremiumAmount;
      this.feeAmount = policyAdminPolicy.policyInfo.feeAmount;
      this.flatExtraAmount = policyAdminPolicy.policyInfo.flatExtraAmount;
      this.paidToDate = this.derivePolicyDate(policyAdminPolicy.policyInfo.paidToDate);
      this.lastUpdatedTimeOnParty = this.derivePolicyDate(policyAdminPolicy.policyInfo.lastUpdatedTimeOnParty);

      if (policyAdminPolicy.policyInfo.ownerName) {
        const names = policyAdminPolicy.policyInfo.ownerName.split(' ');
        this.ownerFirstName = names[0];
        this.ownerLastName = names[names.length - 1];
      }
      this.paymentType = policyAdminPolicy.paymentType;
      this.paymentFrequency = policyAdminPolicy.paymentFrequency;
      this.paymentFrequency = policyAdminPolicy.paymentFrequency;
    }

    // @TODO remove this once cp starts using party data from eventor
    this.owners = policyAdminPolicy.owners || [];

    this.insured = policyAdminPolicy.insured;

    return this;
  }

  private derivePolicyDate(dateString: string): Date {
    return dateString && new Date(dateString);
  }

  private derivePolicyStatus(policyAdminPolicy: PolicyAdminPolicy) {
    const consolidatedStatus = policyAdminPolicy.policyInfo.status;

    if (!consolidatedStatus) {
      throw new Error('Cannot instantiate Policy Admin status without a status');
    }

    const statuses = consolidatedStatus.split(STATUS_DELIMITER);

    if (statuses && statuses.length === 2) {
      this.policyStage = statuses[0].trim();
      this.billingStage = statuses[1].trim();
    } else if (statuses && statuses.length === 3) {
      this.policyStage = statuses[0].trim();
      this.billingStage = statuses[1].trim();
      this.policyGeneralStatus = statuses[2].trim();
    } else {
      this.policyGeneralStatus = 'NotFound';
    }

    const consolidatedStatusCodes = policyAdminPolicy.policyInfo.statusCode;

    if (!consolidatedStatusCodes) {
      throw new Error('Cannot instantiate Policy Admin status without a status');
    }

    const statusCodes = consolidatedStatusCodes.split(STATUS_DELIMITER);
    if (statusCodes && statusCodes.length === 2) {
      this.policyStageCode = statusCodes[0].trim();
      this.billingStageCode = statusCodes[1].trim();
    } else if (statusCodes && statusCodes.length === 3) {
      this.policyStageCode = statusCodes[0].trim();
      this.billingStageCode = statusCodes[1].trim();
      this.policyGeneralStatus = statusCodes[2].trim();
    } else {
      this.policyGeneralStatus = 'NotFound';
    }
    // TODO: HL-4454 - Reevaluating validation with new fall through logic
    // this.validateStages();
  }

  private validateStages(): void {
    if (!this.policyStage || !(this.policyStage in POLICY_STAGES)) {
      throw new Error(`Invalid workflow stage: ${this.policyStage}`);
    }

    if (!this.billingStage || !(this.billingStage in BILLING_STAGES)) {
      throw new Error(`Invalid billing stage: ${this.billingStage}`);
    }
  }

  public getPolicyStage(): string {
    return this.policyStage;
  }

  public getPolicyStageCode(): string {
    return this.policyStageCode;
  }

  public getWorkflowEquivalentStage(): string {
    const combined = this.getCombinedWorkflowCode();
    return WORKFLOW_STATE_MAPPER[combined];
  }

  public getWorkflowEquivalentStageForCoverpathAgent(): string {
    const combined = this.getCombinedWorkflowCode();
    if (this.isNotFound(this.policyGeneralStatus) || !(combined in WORKFLOW_STATE_MAPPER_CP_AGENT)) {
      return POLICY_GENERAL_STATUS_MAPPER_CP_AGENT['NotFound'];
    } else if (this.isDeathClaim(this.policyGeneralStatus)) {
      return WORKFLOW_STATE_MAPPER_CP_AGENT[combined] + ':' + POLICY_GENERAL_STATUS_MAPPER_CP_AGENT['DeathClaim'];
    } else if (this.isWaiverOfPremium(this.policyGeneralStatus)) {
      return WORKFLOW_STATE_MAPPER_CP_AGENT[combined] + ':' + POLICY_GENERAL_STATUS_MAPPER_CP_AGENT['WaiverOfPremium'];
    } else {
      return WORKFLOW_STATE_MAPPER_CP_AGENT[combined];
    }
  }

  public getWorkflowEquivalentStageForCaseDetails(): string {
    const combined = this.getCombinedWorkflowCode();
    if (this.isNotFound(this.policyGeneralStatus) || !(combined in WORKFLOW_STATE_MAPPER_CASE_DETAILS)) {
      // If there is no mapping status in WORKFLOW_STATE_MAPPER_CASE_DETAILS and it starts with `TM`, we show `Policy Canceled by System`
      if (combined?.startsWith('TM')) {
        return WORKFLOW_STATE_MAPPER_CASE_DETAILS[TrackerStatus.PolicyCanceledBySystem];
      }
      return '';
    } else {
      let status = WORKFLOW_STATE_MAPPER_CASE_DETAILS[combined];
      if (this.isWaiverOfPremium(this.policyGeneralStatus)) {
        status += ':' + POLICY_GENERAL_STATUS_MAPPER_CP_AGENT.WaiverOfPremium;
      }
      if (this.isDeathClaim(this.policyGeneralStatus)) {
        status = TrackerStatus.DeathClaimPending;
      }
      return status;
    }
  }
  private getCombinedWorkflowCode(): string {
    if (!this.policyStageCode) {
      return null;
    }
    let combined = this.policyStageCode + ':';
    if (
      ['IF1', 'IF2', 'IF5'].indexOf(this.policyStageCode) !== -1 ||
      (this.policyStageCode === 'IF3' && (this.billingStageCode === 'BL1' || this.billingStageCode === 'BR1'))
    ) {
      combined += this.billingStageCode;
    } else {
      combined += 'ANY';
    }
    return combined;
  }

  public getPolicyStageContent(): string {
    const policyStage = this.policyStageCode;
    const billingStage = this.billingStageCode;

    if (this.isLapsed(billingStage)) {
      return 'gracePeriod';
    }

    const content = this.matchPolicyAndBillingStages(policyStage, billingStage);
    if (content) {
      return content;
    }

    if (this.isTlicPolicyStage(policyStage)) {
      return null;
    }

    if (this.isTlicBillingStage(billingStage)) {
      return null;
    }

    if (this.isCatchall(policyStage)) {
      return 'policyInForce';
    }

    if (this.isNotCovered(policyStage)) {
      return 'notCovered';
    }

    return 'catchallPA';
  }

  matchPolicyAndBillingStages(policyStage: string, billingStage: string) {
    const contentOptions = STATE_MAPPER[policyStage];
    if (typeof contentOptions === 'string') {
      return contentOptions;
    } else if (contentOptions && typeof contentOptions === 'object') {
      const content = contentOptions[billingStage];
      if (content !== undefined) {
        return content;
      }
    }
  }

  isTlicPolicyStage(policyStage: string) {
    const tlicPolicyStages = ['CI1', 'TM3', 'TM4', 'TM7', 'TM9'];
    return tlicPolicyStages.indexOf(policyStage) >= 0;
  }

  isTlicBillingStage(billingStage: string) {
    const tlicBillingStages = ['BS3', 'BS4'];
    return tlicBillingStages.indexOf(billingStage) >= 0;
  }

  isLapsed(billingStage: string) {
    return billingStage === 'BG2';
  }

  isCatchall(policyStage: string) {
    const catchallStages = ['IF1', 'IF2', 'IF3', 'IF4', 'IF5'];
    return catchallStages.indexOf(policyStage) >= 0;
  }

  isNotCovered(policyStage: string) {
    const notCoveredPolicyStages = ['TM1', 'TM2', 'TM3', 'TM5', 'TM6', 'TM7', 'TM8', 'TM9'];
    return notCoveredPolicyStages.indexOf(policyStage) >= 0;
  }

  public getBillingStage(): string {
    return this.billingStage;
  }

  public getBillingStageCode(): string {
    return this.billingStageCode;
  }

  public getBillingStageContent(): string {
    return BILLING_STAGES[this.getBillingStage()];
  }

  public getDuration(): number {
    return this.duration;
  }

  public getNextPaymentDate(): Date {
    return this.nextPaymentDate;
  }

  public getIssuedDate(): Date {
    return this.issuedDate;
  }

  public getTlicEffectiveDate(): Date {
    return this.tlicEffectiveDate;
  }

  public getEffectiveDate(): Date {
    return this.effectiveDate;
  }

  public getTerminationDate(): Date {
    return this.terminationDate;
  }

  public getOwnerFirstName(): string {
    return this.ownerFirstName;
  }

  public getOwnerLastName(): string {
    return this.ownerLastName;
  }

  public getFreeLookEndDate(): Date {
    return this.freeLookEndDate;
  }

  public getGraceStartDate(): Date {
    return this.graceStartDate;
  }

  public getGraceEndDate(): Date {
    return this.graceEndDate;
  }

  public getLapsedDate(): Date {
    return this.lapsedDate;
  }

  public getConversionDate(): Date {
    return this.conversionEndDate;
  }

  public getConversionCreditPercent(): number {
    return this.conversionCreditPercent;
  }

  public getConversionCreditExpirationDate(): Date {
    return this.conversionCreditExpirationDate;
  }

  public getAcceleratedBenefitRider(): boolean {
    return this.acceleratedBenefitRider;
  }

  public getFaceAmount(): number {
    return this.faceAmount;
  }

  public isPAPolicy(): boolean {
    return this.isPolicyAdminPolicy;
  }

  public getPolicyHoldStatus(): string {
    return this.policyHoldStatus;
  }

  public getRateClass(): string {
    return this.rateClass;
  }

  public getWaiverOfPremium(): boolean {
    return this.waiverOfPremium;
  }

  public isPolicyHeld(): boolean {
    return this.policyHoldStatus === 'Held';
  }

  public getOwnerIsEntity(): boolean {
    return this.ownerIsEntity;
  }

  public getHasThirdPartyPayer(): boolean {
    return this.hasThirdPartyPayer;
  }

  public getHasNoPayer(): boolean {
    return this.hasNoPayer;
  }

  public specialHandling(): boolean {
    return this.hasNoPayer || this.hasThirdPartyPayer || this.ownerIsEntity;
  }

  //To Be Used for V2, Individual Widget Blocking
  public allowBeneWidget(): boolean {
    return !this.isPolicyHeld() && !this.ownerIsEntity;
  }

  //To Be Used for V2, Individual Widget Blocking
  public allowBankWidget(): boolean {
    return !this.isPolicyHeld() && !this.ownerIsEntity && !this.hasThirdPartyPayer && !this.hasNoPayer;
  }

  //To Be Used for V2, Individual Widget Blocking
  public allowContactWidget(): boolean {
    return !this.isPolicyHeld() && !this.ownerIsEntity;
  }

  public getResponseStatus(): string {
    return this.responseStatus;
  }

  public getPolicyAdminPolicyId(): string {
    return this.policyAdminPolicyId;
  }

  public getPolicyNumber(): string {
    return this.policyNumber;
  }

  public getBasePremiumAmount(): number {
    return this.basePremiumAmount;
  }

  public getWaiverPremiumAmount(): number {
    return this.waiverPremiumAmount;
  }

  public getSubstandardPremiumAmount(): number {
    return this.substandardPremiumAmount;
  }

  public getFeeAmount(): number {
    return this.feeAmount;
  }

  public getFlatExtraAmount(): number {
    return this.flatExtraAmount;
  }

  public getLastUpdatedTimeOnParty(): Date {
    return this.lastUpdatedTimeOnParty;
  }

  public getPaidToDate(): Date {
    return this.paidToDate;
  }

  public isNotFound(policyGeneralStatus: string): boolean {
    return policyGeneralStatus === 'NotFound';
  }

  public isDeathClaim(policyGeneralStatus: string): boolean {
    return policyGeneralStatus === 'DeathClaim';
  }

  public isWaiverOfPremium(policyGeneralStatus: string): boolean {
    return policyGeneralStatus === 'WaiverOfPremium';
  }

  public getTotalPremium(): number {
    return (
      this.getBasePremiumAmount() +
      this.getFeeAmount() +
      (this.getWaiverOfPremium() ? this.getWaiverPremiumAmount() : 0) +
      this.getFlatExtraAmount() +
      this.getSubstandardPremiumAmount()
    );
  }

  public getPaymentType(): string {
    return this.paymentType;
  }

  public getPaymentFequency(): string {
    return this.paymentFrequency;
  }

  public setPolicyAdminRiders(policyAdminRiders: PolicyAdminRiders): void {
    this.policyAdminRiders = policyAdminRiders;
  }
}
